/**
 * When a scheduled function should run.
 *
 * WHY MATCHING A MINUTE, NOT COMPUTING A NEXT TIME
 *
 * The obvious design stores "next run at" and fires when the clock passes
 * it. It is also the one that goes wrong quietly: a scheduler that misses a
 * tick has to decide whether to fire late, skip, or catch up, and whichever
 * it chooses is wrong for somebody. Worse, "next run" drifts - each
 * computation starts from the last actual run rather than from the
 * schedule, so a job set for every hour on the hour slowly becomes every
 * hour and eleven seconds.
 *
 * So this answers a different question: does this expression MATCH this
 * minute? The scheduler ticks once a minute and asks. A missed tick means a
 * missed run and nothing else - no drift, no catch-up storm after an
 * outage, no state to get wrong. A job that must not be missed is a queue,
 * not a cron.
 *
 * FIVE FIELDS, UTC, NO SECONDS
 *
 * Minute, hour, day of month, month, day of week. Seconds are deliberately
 * absent: this fires once a minute at most, and a six-field expression
 * would promise precision the scheduler cannot deliver.
 *
 * Everything is UTC. A customer in Singapore asking for "every day at 9am"
 * has to write `0 1 * * *`, which is worse for them and better than the
 * alternative - a schedule that silently shifts twice a year when their
 * local clocks change, on a platform that has no idea where they are.
 */
/** The shorthands people expect, because every other cron accepts them. */
const ALIASES = {
    '@hourly': '0 * * * *',
    '@daily': '0 0 * * *',
    '@midnight': '0 0 * * *',
    '@weekly': '0 0 * * 0',
    '@monthly': '0 0 1 * *',
    '@yearly': '0 0 1 1 *',
    '@annually': '0 0 1 1 *',
};
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const FIELDS = [
    { key: 'minute', label: 'minute', bounds: { min: 0, max: 59 } },
    { key: 'hour', label: 'hour', bounds: { min: 0, max: 23 } },
    { key: 'dayOfMonth', label: 'day of month', bounds: { min: 1, max: 31 } },
    { key: 'month', label: 'month', bounds: { min: 1, max: 12, names: MONTHS } },
    { key: 'dayOfWeek', label: 'day of week', bounds: { min: 0, max: 6, names: DAYS } },
];
/**
 * The shortest interval allowed.
 *
 * Not a technical limit - the scheduler ticks every minute either way. It
 * is a cost decision made visible: `* * * * *` is 43,200 invocations a
 * month per function, and somebody should type that deliberately rather
 * than discover it on an invoice. Currently allowed; the constant is here
 * so raising the floor is a one-line change with a test.
 */
export const MIN_INTERVAL_MINUTES = 1;
export function parseCron(input) {
    const trimmed = input.trim().toLowerCase();
    if (trimmed === '')
        return { error: 'a schedule cannot be empty' };
    const expanded = ALIASES[trimmed] ?? trimmed;
    if (expanded.startsWith('@')) {
        return { error: `unknown shorthand '${trimmed}'. Try ${Object.keys(ALIASES).join(', ')}` };
    }
    const parts = expanded.split(/\s+/);
    if (parts.length === 6) {
        return { error: 'six fields means seconds, which are not supported - the shortest interval is a minute' };
    }
    if (parts.length !== 5) {
        return { error: `a schedule has five fields (minute hour day month weekday), got ${parts.length}` };
    }
    const schedule = { source: input.trim() };
    for (const [i, field] of FIELDS.entries()) {
        const parsed = parseField(parts[i], field.bounds);
        if ('error' in parsed)
            return { error: `${field.label}: ${parsed.error}` };
        schedule[field.key] = parsed;
    }
    return schedule;
}
function parseField(text, bounds) {
    if (text === '*')
        return { any: true };
    const values = new Set();
    for (const chunk of text.split(',')) {
        if (chunk === '')
            return { error: 'empty value in a list' };
        // step: <range>/<n>
        const [rangeText = '', stepText, ...rest] = chunk.split('/');
        if (rest.length > 0)
            return { error: `'${chunk}' has more than one step` };
        let step = 1;
        if (stepText !== undefined) {
            step = Number(stepText);
            if (!Number.isInteger(step) || step < 1)
                return { error: `'${stepText}' is not a step` };
        }
        let from;
        let to;
        if (rangeText === '*' || rangeText === '') {
            from = bounds.min;
            to = bounds.max;
        }
        else if (rangeText.includes('-')) {
            const [a, b, ...extra] = rangeText.split('-');
            if (extra.length > 0)
                return { error: `'${rangeText}' is not a range` };
            const lo = toNumber(a, bounds);
            const hi = toNumber(b, bounds);
            if (lo === null || hi === null)
                return { error: `'${rangeText}' is not a range` };
            if (lo > hi)
                return { error: `'${rangeText}' counts backwards` };
            from = lo;
            to = hi;
        }
        else {
            const single = toNumber(rangeText, bounds);
            if (single === null)
                return { error: `'${rangeText}' is not a value` };
            // A bare value with a step means "from here onwards", which is how
            // every other cron reads `5/15`.
            from = single;
            to = stepText === undefined ? single : bounds.max;
        }
        if (from < bounds.min || to > bounds.max) {
            return { error: `values must be ${bounds.min}-${bounds.max}` };
        }
        for (let v = from; v <= to; v += step)
            values.add(v);
    }
    if (values.size === 0)
        return { error: 'matches nothing' };
    return { values };
}
function toNumber(text, bounds) {
    const named = bounds.names?.indexOf(text);
    if (named !== undefined && named >= 0) {
        // Months are 1-based and named from January; weekdays are 0-based from
        // Sunday. The offset is the difference between the two conventions.
        return named + bounds.min;
    }
    if (!/^\d+$/.test(text))
        return null;
    const n = Number(text);
    return Number.isInteger(n) ? n : null;
}
const matches = (field, value) => 'any' in field ? true : field.values.has(value);
/**
 * Does this schedule fire in the minute containing `at`?
 *
 * DAY-OF-MONTH AND DAY-OF-WEEK ARE OR, NOT AND
 *
 * The one genuinely surprising rule in cron, and it is in POSIX: when both
 * day fields are restricted, a match on EITHER fires. `0 0 1 * mon` means
 * the first of the month AND every Monday, not Mondays that fall on the
 * first. Implementing it as AND is the classic bug and produces a job that
 * runs a few times a year instead of weekly.
 */
export function matchesMinute(schedule, at) {
    if (!matches(schedule.minute, at.getUTCMinutes()))
        return false;
    if (!matches(schedule.hour, at.getUTCHours()))
        return false;
    if (!matches(schedule.month, at.getUTCMonth() + 1))
        return false;
    const domRestricted = !('any' in schedule.dayOfMonth);
    const dowRestricted = !('any' in schedule.dayOfWeek);
    const dom = matches(schedule.dayOfMonth, at.getUTCDate());
    const dow = matches(schedule.dayOfWeek, at.getUTCDay());
    if (domRestricted && dowRestricted)
        return dom || dow;
    return dom && dow;
}
/** The minute `at` falls in, as the identity of one scheduled run. */
export function minuteKey(at) {
    return at.toISOString().slice(0, 16);
}
/**
 * The schedule in words.
 *
 * A cron expression is write-only for most people. Showing "every day at
 * 02:30 UTC" beside it is the difference between a dashboard somebody
 * trusts and one they re-derive by hand every time.
 */
export function describeCron(schedule) {
    const every = (f) => 'any' in f;
    const list = (f) => 'any' in f ? '' : [...f.values].sort((a, b) => a - b).join(', ');
    if (every(schedule.minute) && every(schedule.hour))
        return 'every minute';
    if (every(schedule.hour) && !every(schedule.minute)) {
        const m = list(schedule.minute);
        return m === '0' ? 'every hour, on the hour' : `every hour at minute ${m}`;
    }
    const time = !every(schedule.hour) && !every(schedule.minute)
        ? [...schedule.hour.values].sort((a, b) => a - b)
            .map((h) => `${String(h).padStart(2, '0')}:${String([...schedule.minute.values][0] ?? 0).padStart(2, '0')}`)
            .join(' and ')
        : '';
    const dowRestricted = !every(schedule.dayOfWeek);
    const domRestricted = !every(schedule.dayOfMonth);
    const when = dowRestricted
        ? `on ${[...schedule.dayOfWeek.values].sort((a, b) => a - b).map((d) => DAY_NAMES[d] ?? String(d)).join(', ')}`
        : domRestricted
            ? `on day ${list(schedule.dayOfMonth)} of the month`
            : 'every day';
    return `${when} at ${time} UTC`.replace(/\s+/g, ' ').trim();
}
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
//# sourceMappingURL=index.js.map